/** @jsx jsx */

import { find } from 'lodash';
import { Fragment, SyntheticEvent, useCallback } from 'react';

import { Icon } from '@zapier/zinnia-icons';
import { Alert } from '@zapier/design-system';
import { jsx } from '@emotion/core';
import { useLink } from '@zapier/common-hooks';

import ActionsDropdownMenu from 'app/developer-v3/components/ActionsDropdownMenu';
import ClickableTable from 'app/developer-v3/components/ClickableTable';
import LockedNavCog from 'app/developer-v3/components/LockedNavCog';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import { AUTH_SCHEMES } from 'app/developer-v3/constants';
import { isLastDescendantNodeClicked } from 'app/developer-v3/utils';
import {
  CurrentVersionReadOnlyReason,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';

type Props = {
  authType: string;
  editUrl: string;
  isLegacy: boolean;
  onDeleteAuthentication: () => void;
};

const AuthenticationSchemeList = (props: Props) => {
  const { authType, editUrl, isLegacy, onDeleteAuthentication } = props;

  const { routeTo } = useLink();
  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isReadOnly = !!currentVersionReadOnlyReason;
  const isNotAdmin =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.NOT_ADMIN;

  const onClickRow = useCallback(
    (event: SyntheticEvent<HTMLElement>) => {
      if (!isLastDescendantNodeClicked(event)) {
        routeTo(editUrl);
      }
    },
    [editUrl, routeTo]
  );

  return (
    <ManagerLayout
      isButtonDisabled={isReadOnly}
      shouldLimitWidth={true}
      title="Authentication"
    >
      {isLegacy && (
        <Alert status="warning">
          This authentication is converted from your legacy project. Legacy
          triggers and actions on this version depend on it and they may need to
          be re-created if you delete this authentication and reconfigure.
        </Alert>
      )}
      <ClickableTable
        body={[
          [
            {
              value: '',
              element: (
                find(AUTH_SCHEMES, type => type.value === authType) ||
                AUTH_SCHEMES[0]
              ).label,
            },
            {
              value: '',
              element: isNotAdmin ? (
                <LockedNavCog message="Admin access only" />
              ) : (
                !isReadOnly && (
                  <ActionsDropdownMenu
                    items={[
                      {
                        content: (
                          <Fragment>
                            <Icon name="actionEdit" size={16} /> Edit
                          </Fragment>
                        ),
                        onChoose: () => routeTo(editUrl),
                        value: 'edit',
                      },
                      {
                        isConfirmationRequired: true,
                        isDanger: true,
                        content: (
                          <Fragment>
                            <Icon name="actionTrash" size={16} /> Delete
                          </Fragment>
                        ),
                        modalTextContent:
                          'Are you sure you want to delete the auth?',
                        modalTitle: 'Delete auth',
                        onChoose: () => onDeleteAuthentication(),
                        value: 'delete',
                      },
                    ]}
                  />
                )
              ),
            },
          ],
        ]}
        header={[
          { title: 'SCHEME', hasSort: false },
          { title: '', hasSort: false },
        ]}
        onClickRow={onClickRow}
        rowAriaLabel="Click to edit this auth"
      />
    </ManagerLayout>
  );
};

export default AuthenticationSchemeList;
